import {
  AmplitudeGlobal,
  AnalyticsProvider,
  AmplitudeIdentify,
  EventProps,
  UserBackendProps,
  UserId
} from './types'
import * as debugLog from '../debug-log'

/**
 * GenericClient implements the typical `client.track(name, data)` API. It
 * works as-is with Kissmetrics, Mixpanel, Heap, etc.
 */
export class GenericClient implements AnalyticsProvider {
  public constructor(
    public readonly label: string,
    public readonly client?: AnalyticsProvider
  ) {}

  public async track(eventName: string, data?: EventProps): Promise<boolean> {
    if (!this.client) {
      return false
    }
    this.client.track(eventName, data)
    return true
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserId(userId: UserId): Promise<boolean> {
    // `track` looks the same across these "generic" platforms, but the
    // identification calls do not. We'll have to start making more
    // platform-specific clients. For now, Amplitude is our front-runner, so
    // we'll only bother doing it there. We can add more clients later if there
    // are other platforms in the mix.
    return false
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public async setUserProps(props: UserBackendProps): Promise<boolean> {
    // See comment for `setUserId`
    return false
  }
}

/**
 * Amplitude has a slightly different shape, so it gets its own wrapper.
 */
export class AmplitudeClient implements AnalyticsProvider {
  public readonly label: string
  public constructor(public readonly client?: AmplitudeGlobal) {
    this.label = 'Amplitude'
    this.client = client
  }

  public track(eventName: string, data?: EventProps): Promise<boolean> {
    const client = this.client
    if (!client) {
      return Promise.resolve(false)
    }

    // Using a verbose Promise here so we can reject an error within the
    // callback.
    return new Promise((resolve, reject) => {
      try {
        client
          .getInstance()
          .logEvent(
            eventName,
            data,
            (responseCode: number, responseBody: string) => {
              if (responseCode >= 200 && responseCode < 300) {
                return resolve(true)
              }
              if (responseCode === 0) {
                // This is not an error. There's a "No request sent" message,
                // but it's not actually a problem. See
                // https://github.com/amplitude/Amplitude-Javascript/issues/18#issuecomment-423562300.
                return resolve(true)
              }
              return reject(new Error(`[${responseCode}] ${responseBody}`))
            }
          )
      } catch (err) {
        reject(err)
      }
    })
  }

  public async setUserId(userId: UserId): Promise<boolean> {
    if (!this.client) {
      return false
    }
    this.client.getInstance().setUserId(userId)
    return true
  }

  /**
   * https://help.amplitude.com/hc/en-us/articles/115001361248#setting-user-properties
   */
  public async setUserProps(props: UserBackendProps): Promise<boolean> {
    if (!this.client) {
      return false
    }

    let identify: AmplitudeIdentify | undefined
    for (const [key, val] of Object.entries(props)) {
      if (!identify) {
        identify = new this.client.Identify().set(key, val)
      } else {
        identify = identify.set(key, val)
      }
    }
    if (identify) {
      this.client.getInstance().identify(identify)
      return true
    }
    return false
  }
}
